import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit2, Trash2, Image, Eye, EyeOff, GripVertical,
  X, Save, ArrowUp, ArrowDown, ExternalLink
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import ImageUploader from '../components/ImageUploader';

const BannerModal = ({ banner, onClose, onSave }) => {
  const [formData, setFormData] = useState(banner || {
    caption: '',
    title_line1: '',
    title_line2: '',
    title_line3: '',
    subtitle: '',
    background_image_url: '',
    background_position: 'center',
    cta_primary_text: 'Calcular Ahorro',
    cta_primary_link: '/calculadora',
    cta_secondary_text: 'Solicitar Consulta',
    cta_secondary_link: '#contact',
    overlay_opacity: 60,
    accent_color: '#90EE90',
    is_active: true
  });
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave(formData);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl w-full max-w-4xl my-8">
        <div className="sticky top-0 bg-zinc-900 border-b border-zinc-800 p-4 flex items-center justify-between rounded-t-xl">
          <h3 className="text-lg font-semibold text-white">
            {banner ? 'Editar Banner' : 'Nuevo Banner'}
          </h3>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPreviewMode(!previewMode)}
              className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                previewMode ? 'bg-emerald-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'
              }`}
            >
              <Eye className="w-4 h-4 inline mr-1" />
              Preview
            </button>
            <button onClick={onClose} className="text-zinc-400 hover:text-white p-1">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Preview */}
        {previewMode && formData.background_image_url && (
          <div className="relative h-64 overflow-hidden">
            <img
              src={formData.background_image_url}
              alt="Preview"
              className="w-full h-full object-cover"
              style={{ objectPosition: formData.background_position }}
            />
            <div 
              className="absolute inset-0 bg-gradient-to-r from-black via-black/50 to-transparent"
              style={{ opacity: formData.overlay_opacity / 100 }}
            ></div>
            <div className="absolute inset-0 flex items-center p-8">
              <div className="text-white max-w-lg">
                {formData.caption && (
                  <p className="text-sm font-medium uppercase tracking-widest text-zinc-300 mb-2" style={{ color: formData.accent_color }}>
                    {formData.caption}
                  </p>
                )}
                <h2 className="text-3xl font-bold leading-tight">
                  {formData.title_line1}
                  {formData.title_line2 && (
                    <>
                      <br />
                      <span style={{ color: formData.accent_color }}>{formData.title_line2}</span>
                    </>
                  )}
                  {formData.title_line3 && (
                    <>
                      <br />
                      {formData.title_line3}
                    </>
                  )}
                </h2>
                {formData.subtitle && (
                  <p className="mt-3 text-sm text-zinc-300">{formData.subtitle}</p>
                )}
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Imagen de fondo */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-emerald-400 flex items-center gap-2">
              <Image className="w-4 h-4" />
              Imagen de Fondo
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <ImageUploader
                  value={formData.background_image_url}
                  onChange={(url) => setFormData({ ...formData, background_image_url: url })}
                  label="Imagen del Banner *"
                  placeholder="Arrastra una imagen o haz clic para seleccionar"
                />
              </div>
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Posición</label>
                <select
                  value={formData.background_position}
                  onChange={(e) => setFormData({ ...formData, background_position: e.target.value })}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
                >
                  <option value="center">Centro</option>
                  <option value="top">Arriba</option>
                  <option value="bottom">Abajo</option>
                  <option value="left">Izquierda</option>
                  <option value="right">Derecha</option>
                </select>
              </div>
            </div>
          </div>

          {/* Textos */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-emerald-400">Textos del Banner</h4>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Texto superior pequeño</label>
              <input
                value={formData.caption || ''}
                onChange={(e) => setFormData({ ...formData, caption: e.target.value })}
                placeholder="Ej: Energía Solar en Paraguay"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Título - Línea 1 *</label>
                <input
                  value={formData.title_line1}
                  onChange={(e) => setFormData({ ...formData, title_line1: e.target.value })}
                  required
                  placeholder="Ej: Soberanía"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Línea 2 (destacada)</label>
                <input
                  value={formData.title_line2 || ''}
                  onChange={(e) => setFormData({ ...formData, title_line2: e.target.value })}
                  placeholder="Ej: Energética"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Línea 3</label>
                <input
                  value={formData.title_line3 || ''}
                  onChange={(e) => setFormData({ ...formData, title_line3: e.target.value })}
                  placeholder="Ej: para tu Hogar"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Subtítulo / Descripción</label>
              <textarea
                value={formData.subtitle || ''}
                onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                rows={2}
                placeholder="Texto descriptivo debajo del título..."
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
              />
            </div>
          </div>

          {/* Botones CTA */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-emerald-400">Botones de Acción</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3 p-4 bg-zinc-800/50 rounded-lg">
                <p className="text-xs text-zinc-500 uppercase">Botón Principal</p>
                <input
                  value={formData.cta_primary_text || ''}
                  onChange={(e) => setFormData({ ...formData, cta_primary_text: e.target.value })}
                  placeholder="Texto del botón"
                  className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
                />
                <input
                  value={formData.cta_primary_link || ''}
                  onChange={(e) => setFormData({ ...formData, cta_primary_link: e.target.value })}
                  placeholder="Link (ej: /calculadora)"
                  className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
                />
              </div>
              <div className="space-y-3 p-4 bg-zinc-800/50 rounded-lg">
                <p className="text-xs text-zinc-500 uppercase">Botón Secundario</p>
                <input
                  value={formData.cta_secondary_text || ''}
                  onChange={(e) => setFormData({ ...formData, cta_secondary_text: e.target.value })}
                  placeholder="Texto del botón"
                  className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
                />
                <input
                  value={formData.cta_secondary_link || ''}
                  onChange={(e) => setFormData({ ...formData, cta_secondary_link: e.target.value })}
                  placeholder="Link (ej: #contact)"
                  className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
                />
              </div>
            </div>
          </div>

          {/* Configuración visual */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-emerald-400">Configuración Visual</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Opacidad del overlay: {formData.overlay_opacity}%</label>
                <input
                  type="range"
                  value={formData.overlay_opacity}
                  onChange={(e) => setFormData({ ...formData, overlay_opacity: parseInt(e.target.value) })}
                  min="0"
                  max="100"
                  className="w-full h-2 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-emerald-500"
                />
              </div>
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Color de acento</label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={formData.accent_color}
                    onChange={(e) => setFormData({ ...formData, accent_color: e.target.value })}
                    className="w-12 h-10 rounded cursor-pointer bg-zinc-800 border border-zinc-700"
                  />
                  <input
                    value={formData.accent_color}
                    onChange={(e) => setFormData({ ...formData, accent_color: e.target.value })}
                    className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Estado</label>
                <label className="flex items-center gap-2 cursor-pointer mt-2">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 rounded border-zinc-600 text-emerald-500 focus:ring-emerald-500"
                  />
                  <span className="text-sm text-zinc-300">Banner activo</span>
                </label>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-zinc-800">
            <button type="button" onClick={onClose} className="px-4 py-2 text-zinc-400 hover:text-white">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Guardando...' : 'Guardar Banner'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const BannersPage = () => {
  const { fetchWithAuth, hasPermission } = useAuth();
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);

  const canEdit = hasPermission('settings_edit');

  const loadBanners = async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth('/api/banners?include_inactive=true');
      if (res.ok) {
        const data = await res.json();
        setBanners(data);
      }
    } catch (error) {
      console.error('Error cargando banners:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadBanners();
  }, []);

  const handleSaveBanner = async (bannerData) => {
    try {
      const url = editingBanner 
        ? `/api/banners/${editingBanner.id}`
        : '/api/banners';
      
      const res = await fetchWithAuth(url, {
        method: editingBanner ? 'PUT' : 'POST',
        body: JSON.stringify(bannerData)
      });

      if (res.ok) {
        setShowModal(false);
        setEditingBanner(null);
        loadBanners();
      } else {
        const error = await res.json();
        alert(error.detail || 'Error al guardar');
      }
    } catch (error) {
      alert('Error de conexión');
    }
  };

  const handleToggle = async (bannerId) => {
    try {
      const res = await fetchWithAuth(`/api/banners/${bannerId}/toggle`, { method: 'PUT' });
      if (res.ok) {
        loadBanners();
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleDelete = async (bannerId) => {
    if (!confirm('¿Estás seguro de eliminar este banner?')) return;
    
    try {
      const res = await fetchWithAuth(`/api/banners/${bannerId}`, { method: 'DELETE' });
      if (res.ok) {
        loadBanners();
      }
    } catch (error) {
      alert('Error de conexión');
    }
  };

  const handleMove = async (index, direction) => {
    const newBanners = [...banners];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex < 0 || newIndex >= newBanners.length) return;
    
    [newBanners[index], newBanners[newIndex]] = [newBanners[newIndex], newBanners[index]];
    
    const bannerIds = newBanners.map(b => b.id);
    
    try {
      const res = await fetchWithAuth('/api/banners/reorder', {
        method: 'POST',
        body: JSON.stringify(bannerIds)
      });
      if (res.ok) {
        loadBanners();
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleSeedDefault = async () => {
    try {
      const res = await fetchWithAuth('/api/banners/seed', { method: 'POST' });
      if (res.ok) {
        loadBanners();
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  return (
    <div className="space-y-6" data-testid="banners-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Banners del Hero</h1>
          <p className="text-zinc-400 text-sm">Gestiona los banners rotativos de la página principal</p>
        </div>
        {canEdit && (
          <button
            onClick={() => { setEditingBanner(null); setShowModal(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg"
            data-testid="add-banner-btn"
          >
            <Plus className="w-4 h-4" />
            Nuevo Banner
          </button>
        )}
      </div>

      {/* Info */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
        <p className="text-sm text-zinc-400">
          Los banners activos se mostrarán en el hero de la página principal, rotando automáticamente cada 4 segundos.
          Puedes reordenarlos usando las flechas.
        </p>
      </div>

      {/* Banners List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin"></div>
        </div>
      ) : banners.length === 0 ? (
        <div className="text-center py-12 bg-zinc-900/50 border border-zinc-800 rounded-xl">
          <Image className="w-12 h-12 mx-auto mb-3 text-zinc-600" />
          <p className="text-zinc-500 mb-4">No hay banners configurados</p>
          {canEdit && (
            <button
              onClick={handleSeedDefault}
              className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg"
            >
              Crear banner por defecto
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {banners.map((banner, index) => (
            <div
              key={banner.id}
              className={`bg-zinc-900/50 border rounded-xl overflow-hidden transition-all ${
                banner.is_active ? 'border-zinc-800' : 'border-zinc-800/50 opacity-60'
              }`}
            >
              <div className="flex">
                {/* Preview Image */}
                <div className="w-48 h-32 flex-shrink-0 relative">
                  <img
                    src={banner.background_image_url}
                    alt={banner.title_line1}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/40"></div>
                  <span className={`absolute top-2 left-2 px-2 py-0.5 text-xs rounded ${
                    banner.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-500/20 text-zinc-400'
                  }`}>
                    {banner.is_active ? 'Activo' : 'Inactivo'}
                  </span>
                </div>

                {/* Content */}
                <div className="flex-1 p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      {banner.caption && (
                        <p className="text-xs text-zinc-500 uppercase mb-1">{banner.caption}</p>
                      )}
                      <h3 className="text-white font-semibold">
                        {banner.title_line1}
                        {banner.title_line2 && <span style={{ color: banner.accent_color }}> {banner.title_line2}</span>}
                        {banner.title_line3 && ` ${banner.title_line3}`}
                      </h3>
                      {banner.subtitle && (
                        <p className="text-sm text-zinc-400 mt-1 line-clamp-1">{banner.subtitle}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {canEdit && (
                        <>
                          <button
                            onClick={() => handleMove(index, 'up')}
                            disabled={index === 0}
                            className="p-1.5 text-zinc-500 hover:text-white hover:bg-zinc-700 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <ArrowUp className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleMove(index, 'down')}
                            disabled={index === banners.length - 1}
                            className="p-1.5 text-zinc-500 hover:text-white hover:bg-zinc-700 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <ArrowDown className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleToggle(banner.id)}
                            className={`p-1.5 rounded transition-colors ${
                              banner.is_active 
                                ? 'text-emerald-400 hover:bg-emerald-500/10' 
                                : 'text-zinc-500 hover:bg-zinc-700'
                            }`}
                          >
                            {banner.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => { setEditingBanner(banner); setShowModal(true); }}
                            className="p-1.5 text-zinc-500 hover:text-white hover:bg-zinc-700 rounded"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(banner.id)}
                            className="p-1.5 text-zinc-500 hover:text-red-400 hover:bg-red-500/10 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-xs text-zinc-500">
                    {banner.cta_primary_text && (
                      <span className="flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" />
                        {banner.cta_primary_text} → {banner.cta_primary_link}
                      </span>
                    )}
                    <span>Orden: {banner.display_order + 1}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <BannerModal
          banner={editingBanner}
          onClose={() => { setShowModal(false); setEditingBanner(null); }}
          onSave={handleSaveBanner}
        />
      )}
    </div>
  );
};

export default BannersPage;
